/* eslint-disable no-nested-ternary */
import React, { useState, useContext } from "react";

import { motion, AnimatePresence } from "framer-motion";
import Link from "next/link";
import PropTypes from "prop-types";
import styled from "styled-components";

import ComponentContext from "../../../contexts/ComponentContext";
import { useStores } from "../../../contexts/StoreContext";
import useDevice from "../../customHooks/useDevice";
import Markdown from "../../styles/Markdown";
import ViewMoreModal from "./ViewMoreModal";

const DarkOverlay = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
`;

const Container = styled.div`
  position: relative;
  height: calc(${(props) => (props.height || "100%") + props.offset});
  width: 100%;
  overflow: hidden;
  margin-top: ${(props) => props.offset || "0px"};
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: ${(props) => props.objectFit || "cover"};
`;

const Overlay = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  background-color: rgba(0, 0, 0, 0.5);
  padding: 2rem;
`;

const TextContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  height: 100%;
  width: 100%;
`;

const MainText = styled(motion.h2)`
  color: white;
  font-size: 24px;
  font-weight: bold;
  margin: 0;
`;

const AdditionalText = styled(motion.p)`
  color: white;
  font-size: 16px;
  text-align: center;
  max-width: 80%;
  margin: 0;
`;

const Button = styled(motion.button)`
  padding: 0.5rem 1rem;
  background-color: white;
  color: black;
  border: none;
  cursor: pointer;
`;

const Spacer = styled.div`
  height: 1rem;
`;

function ImageWithButton({
  photoUrl,
  initialText,
  hoverText,
  buttonText,
  buttonUrl,
  objectFit,
  offset,
  hasOverlayOnHover,
  popupText,
}) {
  const { isMobile } = useDevice();

  const { boxHeight } = useContext(ComponentContext);
  const [isHovered, setIsHovered] = useState(false);

  const { modalStore } = useStores();
  const { openModal, closeModal } = modalStore;

  const handleClick = (e) => {
    e.preventDefault();
    openModal({
      content: <ViewMoreModal closeModal={closeModal} content={popupText} />,
      fullscreen: false,
      overflowVisible: true,
      size: "xl",
    });
  };

  const containerVariants = {
    center: { y: 0 },
    top: { y: "-15%" },
  };

  const mainTextVariants = {
    center: { y: 0 },
    top: { y: 0 },
  };

  const additionalContentVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0 },
  };

  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  };

  return (
    <Container
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      height={boxHeight}
      offset={offset}
      className="image-with-button"
    >
      <Image src={photoUrl} alt="Background" objectFit={objectFit} />
      <AnimatePresence>
        {(isHovered || isMobile) && hasOverlayOnHover ? (
          <DarkOverlay
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={overlayVariants}
            transition={{ duration: 0.3 }}
            className="image-overlay"
          />
        ) : null}
      </AnimatePresence>
      <Overlay>
        <TextContainer
          initial="center"
          animate={isHovered || isMobile ? "top" : "center"}
          variants={containerVariants}
          transition={{ duration: 0.5 }}
        >
          <Spacer />
          <MainText variants={mainTextVariants}>
            <Markdown>{initialText}</Markdown>
          </MainText>
          <AnimatePresence>
            {isHovered || isMobile ? (
              <AdditionalText
                initial="hidden"
                animate="visible"
                exit="hidden"
                variants={additionalContentVariants}
                transition={{ duration: 0.5 }}
              >
                <Markdown>{hoverText}</Markdown>
              </AdditionalText>
            ) : (
              <Spacer />
            )}
          </AnimatePresence>
          <AnimatePresence>
            {isHovered || isMobile ? (
              popupText ? (
                <Button
                  onClick={handleClick}
                  as={motion.button}
                  initial="hidden"
                  animate="visible"
                  exit="hidden"
                  variants={additionalContentVariants}
                  transition={{ duration: 0.5 }}
                >
                  {buttonText}
                </Button>
              ) : buttonText ? (
                <Link href={buttonUrl} passHref>
                  <Button
                    as={motion.a} // Ensure the button looks like a link
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                    variants={additionalContentVariants}
                    transition={{ duration: 0.5 }}
                  >
                    {buttonText}
                  </Button>
                </Link>
              ) : (
                <Spacer />
              )
            ) : (
              <Spacer />
            )}
          </AnimatePresence>
        </TextContainer>
      </Overlay>
    </Container>
  );
}

export default ImageWithButton;

ImageWithButton.defaultProps = {
  photoUrl: "",
  initialText: "",
  hoverText: "",
  buttonText: "",
  buttonUrl: "",
  objectFit: "cover",
  offset: "0px",
  popupText: "",
  hasOverlayOnHover: true,
};

ImageWithButton.propTypes = {
  photoUrl: PropTypes.string,
  initialText: PropTypes.string,
  hoverText: PropTypes.string,
  buttonText: PropTypes.string,
  buttonUrl: PropTypes.string,
  objectFit: PropTypes.string,
  offset: PropTypes.string,
  hasOverlayOnHover: PropTypes.bool,
  popupText: PropTypes.string,
};
